import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { Plus, Search } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/Card';
import Modal from '@/components/Modal';
import { StatusBadge, PriorityBadge } from '@/components/Badge';
import Loader, { EmptyState } from '@/components/Loader';
import Pagination from '@/components/Pagination';
import { taskService, projectService, userService } from '@/services';
import { useAuth } from '@/hooks/useAuth';
import { fmtDate } from '@/utils/format';

export default function Tasks() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ status: '', priority: '', search: '' });
  const [page, setPage] = useState(1);
  const [showNew, setShowNew] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['tasks', filters, page],
    queryFn: () => taskService.list({ ...filters, page, limit: 10 }),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tasks"
        subtitle="Anyone can create a task and assign it to a teammate"
        actions={
          <button className="btn-primary" onClick={() => setShowNew(true)}>
            <Plus size={16} /> New task
          </button>
        }
      />

      <Card>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-3 text-slate-400" />
            <input
              placeholder="Search tasks..."
              className="input pl-9 w-60"
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            />
          </div>
          <select className="input w-40" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="in-progress">In progress</option>
            <option value="completed">Completed</option>
            <option value="on-hold">On hold</option>
          </select>
          <select className="input w-40" value={filters.priority} onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value }))}>
            <option value="">All priority</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>

        {isLoading ? (
          <Loader />
        ) : (data?.items || []).length === 0 ? (
          <EmptyState title="No tasks found" subtitle="Try adjusting your filters" />
        ) : (
          <div className="table-wrap">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className="th">Title</th>
                  <th className="th">Project</th>
                  <th className="th">Assignee</th>
                  <th className="th">Assigned by</th>
                  <th className="th">Priority</th>
                  <th className="th">Deadline</th>
                  <th className="th">Status</th>
                  <th className="th">Progress</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((t) => (
                  <tr key={t._id} className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50" onClick={() => navigate(`/tasks/${t._id}`)}>
                    <td className="td font-medium">{t.title}</td>
                    <td className="td">{t.project?.name}</td>
                    <td className="td">{t.assignee?.name || '—'}</td>
                    <td className="td">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white grid place-items-center text-xs font-semibold">
                          {t.createdBy?.name?.[0]?.toUpperCase() || '?'}
                        </div>
                        <span className="text-sm">{t.createdBy?.name || '—'}</span>
                      </div>
                    </td>
                    <td className="td"><PriorityBadge priority={t.priority} /></td>
                    <td className="td">{t.deadline ? fmtDate(t.deadline) : '—'}</td>
                    <td className="td"><StatusBadge status={t.status} /></td>
                    <td className="td">
                      <div className="flex items-center gap-2 w-24">
                        <div className="flex-1 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                          <div className="h-full bg-brand-500" style={{ width: `${t.completionPercent || 0}%` }} />
                        </div>
                        <span className="text-xs text-slate-500">{t.completionPercent || 0}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination page={page} limit={10} total={data?.total || 0} onChange={setPage} />
      </Card>

      <NewTaskModal
        open={showNew}
        onClose={() => setShowNew(false)}
        onCreated={() => {
          qc.invalidateQueries({ queryKey: ['tasks'] });
          setShowNew(false);
        }}
      />
    </div>
  );
}

function NewTaskModal({ open, onClose, onCreated }) {
  const { user } = useAuth();
  const { register, handleSubmit, reset, setValue, watch, formState: { isSubmitting } } = useForm({
    defaultValues: { priority: 'medium', status: 'pending', assignee: '' },
  });
  const projects = useQuery({ queryKey: ['projects', 'all'], queryFn: () => projectService.list({ limit: 100 }), enabled: open });
  const users = useQuery({ queryKey: ['users', 'all'], queryFn: () => userService.list({ limit: 200 }), enabled: open });
  const userList = users.data?.items || [];
  const currentAssignee = watch('assignee');

  const onSubmit = async (data) => {
    try {
      await taskService.create({
        ...data,
        completionPercent: 0,
        estimatedHours: data.estimatedHours ? Number(data.estimatedHours) : 0,
      });
      toast.success('Task created');
      reset();
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Create task" size="lg" footer={
      <>
        <button onClick={onClose} className="btn-secondary">Cancel</button>
        <button form="taskForm" type="submit" className="btn-primary" disabled={isSubmitting}>
          {isSubmitting ? 'Creating...' : 'Create task'}
        </button>
      </>
    }>
      <form id="taskForm" onSubmit={handleSubmit(onSubmit)} className="grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="label">Title</label>
          <input className="input" {...register('title', { required: true })} />
        </div>
        <div className="sm:col-span-2">
          <label className="label">Description</label>
          <textarea rows={3} className="input" {...register('description')} />
        </div>
        <div>
          <label className="label">Project</label>
          <select className="input" {...register('project', { required: true })}>
            <option value="">Select project</option>
            {(projects.data?.items || []).map((p) => (
              <option key={p._id} value={p._id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div>
          <div className="flex items-center justify-between">
            <label className="label">Assignee</label>
            {user?._id && (
              <button
                type="button"
                onClick={() => setValue('assignee', user._id, { shouldDirty: true })}
                className="text-xs text-brand-600 hover:underline"
              >
                Assign to me
              </button>
            )}
          </div>
          <select className="input" {...register('assignee')}>
            <option value="">Unassigned</option>
            {users.isLoading && <option disabled>Loading teammates...</option>}
            {userList.map((u) => (
              <option key={u._id} value={u._id}>
                {u.name}{u._id === user?._id ? ' (You)' : ''}{u.department ? ` — ${u.department}` : ''}
              </option>
            ))}
          </select>
          {!users.isLoading && userList.length === 0 && (
            <p className="text-xs text-rose-500 mt-1">No teammates found. Ask an admin to add employees.</p>
          )}
          {currentAssignee === user?._id && (
            <p className="text-xs text-slate-500 mt-1">You'll own this task.</p>
          )}
        </div>
        <div>
          <label className="label">Priority</label>
          <select className="input" {...register('priority')}>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>
        <div>
          <label className="label">Deadline</label>
          <input type="date" className="input" {...register('deadline')} />
        </div>
        <div>
          <label className="label">Estimated hours</label>
          <input type="number" min={0} className="input" {...register('estimatedHours')} />
        </div>
      </form>
    </Modal>
  );
}
